// Desktop localStorage persistence: the wind briefing and the best score. The
// failure-proof reading and writing is the shared storage kit (src/shared/
// storage-kit.js), also used by the phone version.

import {
  TIME_SLOTS, COMPASS, MAX_WIND_INPUT, PACES, DEFAULT_PACE, SEASON_DAYS_MAX,
} from '../config/constants.js';
import { loadJSON, saveJSON } from '../shared/storage-kit.js';
import { NEW_CAREER, upgradeCareer } from '../core/career.js';
import { FREE_DEFAULTS, isValidFree } from '../core/freeplay.js';

const WIND_KEY = 'farmatc.wind.v1';
const BEST_KEY = 'farmatc.best.v1';
const CAREER_KEY = 'farmatc.career.v1';
const SETTINGS_KEY = 'farmatc.settings.v1';
const VALID_DIRS = new Set(COMPASS.map((c) => c.deg));

export function isValidWind(wind) {
  return Boolean(wind) && TIME_SLOTS.every((slot) => {
    const w = wind[slot];
    return w
      && typeof w.speed === 'number' && w.speed >= 0 && w.speed <= MAX_WIND_INPUT
      && VALID_DIRS.has(w.dir);
  });
}

/** The saved Wind Briefing, or null when there's none (or it's unreadable). */
export function loadWind(storage = globalThis.localStorage) {
  return loadJSON(WIND_KEY, isValidWind, storage);
}

export function saveWind(wind, storage = globalThis.localStorage) {
  // storage full or blocked: the briefing just won't survive a reload
  saveJSON(WIND_KEY, wind, storage);
}

/** The best shift score saved on this device, or null when there's none yet. */
export function loadBestScore(storage = globalThis.localStorage) {
  const saved = loadJSON(BEST_KEY, (v) => Boolean(v) && Number.isFinite(v.score), storage);
  return saved ? Math.round(saved.score) : null;
}

/** Save a new best shift score; false when storage is full or blocked. */
export function saveBestScore(score, storage = globalThis.localStorage) {
  return saveJSON(BEST_KEY, { score: Math.round(score), at: new Date().toISOString() }, storage);
}

/**
 * Career progress (points, shifts, Lesson 1, level, season day); a fresh
 * career when none is saved. A 0.0.3 save gains the newer fields.
 */
export function loadCareer(storage = globalThis.localStorage) {
  const saved = loadJSON(CAREER_KEY, (c) => upgradeCareer(c) !== null, storage);
  return saved ? upgradeCareer(saved) : { ...NEW_CAREER };
}

export function saveCareer(career, storage = globalThis.localStorage) {
  return saveJSON(CAREER_KEY, career, storage);
}

// Player settings (#13): speed of play and season length.
export const DEFAULT_SETTINGS = Object.freeze({ pace: DEFAULT_PACE, seasonDays: SEASON_DAYS_MAX });

export function isValidSettings(v) {
  return Boolean(v) && PACES.some((p) => p.value === v.pace)
    && Number.isInteger(v.seasonDays) && v.seasonDays >= 1 && v.seasonDays <= SEASON_DAYS_MAX;
}

export function loadSettings(storage = globalThis.localStorage) {
  return loadJSON(SETTINGS_KEY, isValidSettings, storage) || { ...DEFAULT_SETTINGS };
}

export function saveSettings(settings, storage = globalThis.localStorage) {
  return saveJSON(SETTINGS_KEY, settings, storage);
}

// Free play switches (#46), remembered between sessions.
const FREE_KEY = 'farmatc.free.v1';

export function loadFree(storage = globalThis.localStorage) {
  return loadJSON(FREE_KEY, isValidFree, storage) || { ...FREE_DEFAULTS };
}

export function saveFree(free, storage = globalThis.localStorage) {
  return saveJSON(FREE_KEY, free, storage);
}
